/**
 * What an audit action string means on screen.
 *
 * The ledger stores actions as dotted keys - "client.updated", "invoice.sent" -
 * because that is what a filter and an index can work with. A reader needs a
 * sentence. This file is the seam between the two, and it is deliberately React
 * free so the rules can be tested without rendering anything.
 *
 * The same shape `activityType.ts` uses, and for the same reason: an unknown
 * value is shown in a readable form rather than as a missing translation key.
 * New actions arrive from the API whenever a route is instrumented, and a screen
 * that printed "crmLog.action.ticket_replied" the day somebody added one would
 * look broken to the operator and fine to everybody who could fix it.
 */

/**
 * The i18n key for one action.
 *
 * FLAT, with every dot turned into an underscore, and that is not a style
 * choice. `t()` walks the catalogue by dots, so a nested "mail" -> "sent" entry
 * could never coexist with a plain "mail" string one level up - the second one
 * added would silently win or silently vanish. The same trap costs the platform
 * a permission key (`view:crm` is a string, so `view:crm.logs` can have no
 * label at all). Flat slugs cannot collide.
 */
export function auditSlug(action: string): string {
  return action.replace(/\./g, "_");
}

/**
 * The module an action belongs to: the word before the first dot.
 *
 * This is what the Settings screen groups and filters by, which is why the
 * server strips the platform log's "crm." prefix on the way into this ledger -
 * with it, every row in the CRM would be in a module called "crm".
 */
export function auditModule(action: string): string {
  const i = action.indexOf(".");
  return i === -1 ? action : action.slice(0, i);
}

/**
 * The last resort when an action has no translation: the key made readable.
 *
 * "ticket.message.deleted" -> "ticket message deleted". Not a sentence anybody
 * would write, and better than a dotted key: it names the thing that happened,
 * and it makes the missing translation obvious to whoever can add one.
 */
export function humaniseAction(action: string): string {
  return action.replace(/[._]/g, " ").trim();
}

/** `t` as the app hands it over: a key in, a string out. */
type Translate = (key: string) => string;

export function auditActionLabel(action: string, t: Translate): string {
  const key = `crmLog.action.${auditSlug(action)}`;
  const label = t(key);
  // `t` returns the key itself when nothing is catalogued - see i18n/message.ts.
  return label === key ? humaniseAction(action) : label;
}

/** One field that changed, as the ledger recorded it. */
export interface AuditChangeLine {
  field: string;
  before: unknown;
  after: unknown;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

/**
 * The `{before, after}` pair unpacked into one line per changed field.
 *
 * Returns an empty array for anything that is not that shape, and the caller
 * then shows the raw value. `meta` is a free Json column written by two dozen
 * call sites: most of them pass a diff, some pass a bare fact ("recipients: 4"),
 * and a reader is better served by seeing that fact than by a screen that
 * decided it was not a diff and showed nothing.
 */
export function auditChangeLines(meta: unknown): AuditChangeLine[] {
  if (!isRecord(meta)) return [];
  const { before, after } = meta as { before?: unknown; after?: unknown };
  if (!isRecord(before) && !isRecord(after)) return [];
  const fields = [
    ...new Set([...(isRecord(before) ? Object.keys(before) : []), ...(isRecord(after) ? Object.keys(after) : [])]),
  ];
  return fields.map((field) => ({
    field,
    before: isRecord(before) ? (before[field] ?? null) : null,
    after: isRecord(after) ? (after[field] ?? null) : null,
  }));
}

/**
 * One stored value, as a string a cell can hold.
 *
 * Long values are cut rather than wrapped: this runs inside a two-column diff
 * and a pasted address block would push the "after" side off the card. The
 * whole value is still in the row's raw view.
 */
export function auditValueText(value: unknown, max = 120): string {
  if (value === null || value === undefined || value === "") return "-";
  const raw = typeof value === "string" ? value : JSON.stringify(value);
  if (raw === undefined) return "-";
  return raw.length > max ? `${raw.slice(0, max - 1)}…` : raw;
}

/**
 * The changed fields in one line, for a row that has not been opened.
 *
 * Two fields at most, then a count. A save that touched nine columns is
 * information ("somebody re-keyed this record"); nine columns spelled out is a
 * paragraph inside a list row, and the full diff is one click away either way.
 *
 * "->" rather than an arrow glyph, deliberately: this string is read in a
 * 12-pixel muted line where a thin arrow disappears.
 */
export function auditChangeSummary(lines: AuditChangeLine[], max = 2): string | null {
  if (lines.length === 0) return null;
  const shown = lines
    .slice(0, max)
    .map((l) => `${l.field}: ${auditValueText(l.before, 30)} -> ${auditValueText(l.after, 30)}`)
    .join(", ");
  const rest = lines.length - max;
  return rest > 0 ? `${shown} (+${rest})` : shown;
}
